import { UserRole } from 'app/core/authentication';

type SupportedUserRoles = UserRole.student | UserRole.teacher | UserRole.parent | undefined;
type SupportedContexts = 'Login';
type SupportedFunction = 'SSO-' | 'UserInfo-' | 'StudentLogin-' | 'OneTimeLink-' | 'ResetPassword-' | 'Language-' | 'SignUp-' | '';
type SupportedActions = 'Attempt' | 'Authenticated' | 'Click' | 'Change' | 'Entered' |
  'Failure' | 'Request' | 'Resent' | 'Sent' | 'Success';

export type DynamicEventLabels =
  `${SupportedUserRoles}-${SupportedActions}` |
  `${SupportedUserRoles}-${SupportedContexts}-${SupportedFunction}${SupportedActions}`;

export enum DataTrackLabelEnum {
  ParentLearnMoreCta = 'Parent-LearnMoreCta',
  ParentSeeProgress = 'Parent-SeeProgress',
  ParentSeeProgressBar = 'Parent-SeeProgressBar',
  ParentSeeWorkshopSection = 'Parent-SeeWorkshopSection',
  ParentGuardianItemsPageLand = 'Parent-GuardianItemsPageLand',
  ParentSelectSubscriptionCta = 'Parent-SelectSubscriptionCta',
  ParentClickPremiumCheer = 'Parent-ClickPremiumCheer',
  ParentSWCta = 'Parent-SWCta',
  ParentSeeMoreCta = 'Parent-SeeMoreCta',
  ParentSWViewAllCta = 'Parent-SWViewAllCta',
  ParentSWJoinClass = 'Parent-SWJoinClass',
  ParentSWRegister = 'Parent-SWRegister',
  ParentSWSchedule = 'Parent-SWSchedule',
  ParentClickItem = 'Parent-ClickItem',
  ParentMultiplayerToggle = 'Parent-MultiplayerToggle',
  HomeworkTogglePressed = 'homework_toggle_pressed',

  // Game
  WebGLNotSupported = 'webgl-not-supported',
  GameItemsMarketView = 'item_market_view',
  GameItemsMarketLeave = 'item_market_leave',
  GameItemsMarketPurchasing = 'item_purchased',
  GameItemsMarketUseItem = 'press_go_to_edit_bot_button',

  GameEditBotsTabSelected = 'tab_selected',
  GameEditBotsGearItemSelected = 'gear_selected',
  GameEditBotsRoleHatSelected = 'rolehat_selected',
  GameEditBotsPaintSelected = 'paint_selected',
  GameEditBotsStatBarClicked = 'statbar_clicked',
  GameEditBotsGearSkillIconClicked = 'skill_icon_clicked',
  GameEditBotsLoadoutUpdated = 'load_out_updated',

  // remove old events?
  GameEditBotChangePart = 'change_part',
  GameEditBotApplySwatch = 'apply_colour_swatch',
  GameEditBotViewCore = 'view_edit_core_screen',
  GameEditBotChangeCoreSkill = 'change_role_hat',

  GameMarketGearPreviewed = 'Student-ZoGearPartPreviewed',
  GameMarketGearPurchased = 'Student-ZoGearPartPurchased',
  GameNotEnoughCurrencyModalShown = 'Student-NotEnoughCurrencyModalShown',
  NotEnoughCurrencyModalAction = 'Student-NotEnoughCurrencyModalAction',
  GameCosmeticItemPurchased = 'Student-CosmeticItemPurchased',
  MarketNextRotationViewed = 'Student-NextRotationViewed',
  StudentAllDayMarketViewed = 'Student-AllDayMarketViewed',
  StudentAfterSchoolMarketViewed = 'Student-AfterSchoolMarketViewed',
  StudentSeasonalMarketViewed = 'Student-SeasonalMarketViewed',
  StudentCurrencyOverviewViewed = 'Student-CurrencyOverviewViewed',
  StudentGuardianChestUnlockRequest = 'Student-GuardianChestUnlockRequest',
  StudentGuardianChestRewardRedeemed = 'Student-GuardianChestRewardRedeemed',

  GamePartsShopView = 'part_shop_view',
  GamePartsShopPreview = 'part_preview',
  GamePartsShopPurchasing = 'part_purchased',
  GameLaunchBayAskMissionView = 'Student-AskMissionButtonViewed',
  GameLaunchBayAskForMission = 'Student-AskForMission',
  GameLaunchBayMissionTowerSelect = 'mission_tower_selected',
  GameDailyLoginModalView = 'view_daily_home_login_reward_modal',
  StudentBotSelected = 'starter_bot_selected',
  StudentRemindCredentials = 'Student-LoginRemindCta',
  StudentNoParentToShare = 'Student-NoParentShareMilestones',
  StudentArView3DClicked = 'Student-ARBadge-View3DClicked',
  StudentArView3D = 'Student-ARBadge-ModelViewed',
  StudentArViewQr = 'Student-ARBadge-QRCodeViewed',
  StudentArViewAr = 'Student-ARBadge-ARViewed',
  StudentArUnsupported = 'Student-ARBadge-UnsupportedDisplayed',
  MinistryFeedbackQuestionTeacherMission = 'Teacher-Missions-MinistryReporting-Question',
  MinistryFeedbackQuestionTeacherSupport = 'Teacher-TeacherSupports-MinistryReporting-Question',
  MinistryFeedbackQuestionParentCheer = 'Parent-Cheers-MinistryReporting-Question',
  MinistryFeedbackQuestionStudentGameshow = 'Student-GameShow-MinistryReporting-Question',
  TeacherInviteParent = 'Teacher-InviteParent',
  TeacherPrintParentInvitations = 'Teacher-PrintParentInvitations',
  TeacherDownloadLogininfo = 'Teacher-DownloadLogininfo',
  TeacherSupports = 'Teacher-Supports',
  TeacherRegistrationDropOff = 'Teacher-Registration-DropOff',
  TeacherRegistration = 'Teacher-Registration',
  PageViewed = 'PageViewed',
  // Page views
  StudentPageViewedHome = 'Student-PageViewed-Home',
  StudentPageViewedLaunchbay = 'Student-PageViewed-Launchbay',
  StudentPageViewedArcade = 'Student-PageViewed-Arcade',
  StudentPageViewedBlocker = 'Student-PageViewed-Blocker',
  TeacherPageViewedGsSettings = 'Teacher-PageViewed-GS-Settings',
  TeacherPageViewedGsOldSettings = 'Teacher-PageViewed-GS-Old-Settings',
  TeacherPageViewedReports = 'Teacher-PageViewed-Reports',
  TeacherPageViewedFocusAreas = 'Teacher-PageViewed-FocusAreas',
  TeacherPageViewedMain = 'Teacher-PageViewed-Main',
  TeacherPageViewedStudents = 'Teacher-PageViewed-Students',
  TeacherGameShowCompleted = 'Teacher-GameShowCompleted',
  PlatformVersionMismatchReload = 'Platform-Version-Mismatch-Reload',
  PlatformVersionMismatchToast = 'Platform-Version-Mismatch-Toast',
  SessionTimeoutClicked = 'Session-Timeout-Clicked',
  SessionTimeoutSeen = 'Session-Timeout-Seen',
  // Student performance / "on the ground" experience events (unsampled, fire for all)
  StudentPerfMain = 'Student-Perf-Main',
  StudentPerfQView = 'Student-Perf-Q-View',
  StudentPerfQSubmit = 'Student-Perf-Q-Submit',
  // District Dashboard
  DistrictViewPerformanceData = 'District-ViewPerformanceData',
  SchoolViewPerformanceData = 'School-ViewPerformanceData',
  DashboardPerformanceDataViewedPage = 'Dashboard-PerformanceData-ViewedPage',
  AdminDashboardFilterbyTime = 'Admin-Dashboard-FilterbyTime',
  AdminCalendarCreated = 'Admin-CalendarCreated',
  AdminCalendarAssigned = 'Admin-CalendarAssigned',
  // Puzzles
  PuzzleTimeToPlay = 'Puzzle-TimeToPlay',
  PuzzleIncorrectAttempts = 'Puzzle-IncorrectAttempts',
  PuzzleInactiveSubmits = 'Puzzle-InactiveSubmits',
  ActivityMarketingSignup = 'ActivityMarketing-Signup',
  ActivityMarketingView = 'ActivityMarketing-View',
  // ClassOS Pro
  TeacherClassOSProUpsell = 'Teacher-ClassOSPro-Upsell',
  // Co Teacher
  TeacherAddCoteacher = 'Teacher-AddCoteacher',
  TeacherRemoveCoteacher = 'Teacher-RemoveCoteacher',
  // Student Login
  StudentQRCodeLogin = 'Student-QRCodeLogin',
  StudentUsernamePasswordLogin = 'Student-UsernamePasswordLogin',
  StudentThirdPartyLogin = 'Student-ThirdPartyLogin',
  // Student join
  StudentClasscodeJoin = 'Student-ClasscodeJoin',
  StudentThirdPartyJoin = 'Student-ThirdPartyJoin',
  // Game selection
  StudentJoinLobbyNumber = 'Student-LobbyNumber',
  StudentGameSelectionAccessed = 'Student-GameSelectionScreenAccessed',
  StudentPlayTutorial = 'Student-PlayTutorial',
  StudentPlaySoloButton = 'Student-PlaySoloButton',
  StudentPlayWithFriendsButton = 'Student-PlayWithFriendsButton',
  // Gap Changes
  TeacherGapAssignPostAssessmentClicked = 'Teacher-Gap-AssignPostAssessmentClicked',
  TeacherGapMeasureGrowthClicked = 'Teacher-Gap-MeasureGrowthClicked',
  TeacherGapReportBlockViewed = 'Teacher-Gap-ReportBlockViewed',
  TeacherGapListingBoxEntered = 'Teacher-Gap-GapListingBoxEntered',
  TeacherGapInterventionSurveyAnswered = 'Teacher-Gap-InterventionSurveyAnswered',
  TeacherGapGapModalViewed = 'Teacher-Gap-GapModalViewed',
  TeacherGapStruggleReasonsViewed = 'Teacher-Gap-StruggleReasonsViewed',
  // Game Show
  GSPerfCLTeacherStartCalledEvent = 'GS-PERF-CL-TEACHER-StartCalledEvent',
  GSPerfCLTeacherStartReceivedEvent = 'GS-PERF-CL-TEACHER-StartReceivedEvent',
  GSPerfCLTeacherSendInvitesEvent = 'GS-PERF-CL-TEACHER-SendInvitesEvent',
  GSPerfCLTeacherStudentJoinedLobbyEvent = 'GS-PERF-CL-TEACHER-StudentJoinedLobbyEvent',
  GSPerfCLTeacherNextQuestionCalledEvent = 'GS-PERF-CL-TEACHER-NextQuestionCalledEvent',
  GSPerfCLTeacherNextQuestionReceivedEvent = 'GS-PERF-CL-TEACHER-NextQuestionReceivedEvent',
  GSPerfCLTeacherStudentAnsweredQuestionEvent = 'GS-PERF-CL-TEACHER-StudentAnsweredQuestionEvent',
  GSPerfCLTeacherCloseQuestionCalledEvent = 'GS-PERF-CL-TEACHER-CloseQuestionCalledEvent',
  GSPerfCLTeacherCloseQuestionReceivedEvent = 'GS-PERF-CL-TEACHER-CloseQuestionReceivedEvent',
  GSPerfCLTeacherLeaderBoardCalledEvent = 'GS-PERF-CL-TEACHER-LeaderBoardCalled',
  GSPerfCLTeacherLeaderBoardReceivedEvent = 'GS-PERF-CL-TEACHER-LeaderBoardReceived',

  GSPerfCLStudentInviteReceivedEvent = 'GS-PERF-CL-STUDENT-InviteReceivedEvent',
  GSPerfCLStudentJoinCalledEvent = 'GS-PERF-CL-STUDENT-JoinCalledEvent',
  GSPerfCLStudentJoinReceivedEvent = 'GS-PERF-CL-STUDENT-JoinReceivedEvent',
  GSPerfCLStudentJoinedLobbyEvent = 'GS-PERF-CL-STUDENT-JoinedLobbyEvent',
  GSPerfCLStudentNextQuestionStartedEvent = 'GS-PERF-CL-STUDENT-NextQuestionStartedEvent',
  GSPerfCLStudentNextQuestionCalledEvent = 'GS-PERF-CL-STUDENT-NextQuestionCalledEvent',
  GSPerfCLStudentNextQuestionReceivedEvent = 'GS-PERF-CL-STUDENT-NextQuestionReceivedEvent',
  GSPerfCLStudentSubmitQuestionCalledEvent = 'GS-PERF-CL-STUDENT-SubmitQuestionCalledEvent',
  GSPerfCLStudentSubmitQuestionReceivedEvent = 'GS-PERF-CL-STUDENT-SubmitQuestionReceivedEvent',
  GSPerfCLStudentCountdownFinishedEvent = 'GS-PERF-CL-STUDENT-CountdownFinishedEvent',
  GSPerfCLStudentFinishedRenderingEvent = 'GS-PERF-CL-STUDENT-FinishedRenderingEvent',

  // Student Events for activities

  StudentFlagAnIssue = 'Student-FlagAnIssue',
  StudentReload = 'Student-Reload',
  StudentReadAloud = 'Student-ReadAloud',
  StudentHint = 'Student-Hint',
  StudentWorkedExample = 'Student-WorkedExample',
  StudentCalc = 'Student-Calc',

  StudentViewsArticle = 'Student-ViewsArticle',
  StudentChangesArticle = 'Student-ChangesArticle',
  StudentHyperlinkBttnClicked = 'Student-HyperlinkBttnClicked',
  TeacherHyperlinkBttnClicked = 'Teacher-HyperlinkBttnClicked',
  StudentArticleViewedInTheWidget = 'Student-ArticleViewedInTheWidget',
  TeacherArticleViewedInTheWidget = 'Teacher-ArticleViewedInTheWidget',
  StudentGiftClaimed = 'Student-GiftClaimed',
  StudentArticleFullModalDismissed = 'Student-ArticleFullModalDismissed',

  // Navigation
  TeacherResourcesMenuClick = 'Teacher-ResourcesMenuClick',

  // Arcade
  StudentArcadeGameSelected = 'Student-ArcadeGameSelected',

  // Garden Missions
  StudentSeedSelected = 'Student-SeedSelected',
  StudentViewedClassroomGarden = 'Student-ViewedClassroomGarden',
  StudentViewedCelebrationCutscene = 'Student-ViewedCelebrationCutscene',
  StudentViewedOpeningCutscene = 'Student-ViewedOpeningCutscene',
  StudentClickedOnWeeklyRewardButton = 'Student-ClickedOnWeeklyRewardButton',
  StudentHarvested = 'Student-Harvested',
  StudentViewedRetiredPlant = 'Student-ViewedRetiredPlant',
  StudentClickedDropDownFloorMenu = 'Student-ClickedDropDownFloorMenu',
  StudentViewedTutorialModal = 'Student-ViewedTutorialModal',
  StudentClickedSkipCutsceneButton = 'Student-ClickedSkipCutsceneButton',

  // Classroom Focus Modal
  StudentClassroomFocusModalAppeared = 'Student-ClassroomFocusModalAppeared',
  StudentClassroomFocusModalDismissed = 'Student-ClassroomFocusModalDismissed',
  StudentClassroomFocusModalGoClicked = 'Student-ClassroomFocusModalGoClicked',

  // Student Home
  StudentSolveTabBannerClicked = 'Student-SolveTabBannerClicked',

  // Tutorial
  StudentTutorialEnded = 'Student-Tutorial-Ended',

  PrincipalPesSlcModalOpen = 'Principal-PesSlcModalOpen',
  PrincipalPesSlcCheerSent = 'Principal-PesSlcCheerSent',
  PrincipalPesMathalonModalOpen = 'Principal-PesMathalonModalOpen',
  PrincipalPesMathalonCheerSent = 'Principal-PesMathalonCheerSent',
  TeacherPesCheerBadge = 'Teacher-PesCheerBadge',
  TeacherPesCheerModalOpen = 'Teacher-PesCheerModalOpen'
}
